import { create } from 'zustand';
import type { Capabilities, Conversation, Message, PeerInfo, ServerEvent } from '../shared/types';

function sortConversations(list: Conversation[]): Conversation[] {
  return [...list].sort((a, b) => {
    if (Boolean(a.pinned) !== Boolean(b.pinned)) return a.pinned ? -1 : 1;
    return b.lastMessageAt - a.lastMessageAt;
  });
}

function mergeMessage(list: Message[], message: Message): Message[] {
  const index = list.findIndex((item) => item.id === message.id);
  if (index === -1) {
    return [...list, message].sort((a, b) => a.ts - b.ts);
  }
  const next = [...list];
  next[index] = { ...next[index], ...message };
  return next;
}

export interface ChatState {
  connected: boolean;
  capabilities: Capabilities | null;
  conversations: Conversation[];
  messages: Record<string, Message[]>;
  peers: PeerInfo[];
  activeId: string | null;
  settingsOpen: boolean;
  aiMode: boolean;
  mobileChat: boolean;
  toast: { text: string; kind: 'info' | 'error' } | null;
  setConnected: (connected: boolean) => void;
  setCapabilities: (capabilities: Capabilities) => void;
  setConversations: (conversations: Conversation[]) => void;
  setActive: (id: string | null) => void;
  setMessages: (conversationId: string, messages: Message[]) => void;
  upsertConversation: (conversation: Conversation) => void;
  removeConversation: (id: string) => void;
  upsertMessage: (message: Message) => void;
  setPeers: (peers: PeerInfo[]) => void;
  toggleSettings: (open?: boolean) => void;
  toggleAiMode: () => void;
  markRead: (conversationId: string) => void;
  addEphemeral: (conversationId: string, messageId: string) => void;
  appendStream: (conversationId: string, messageId: string, text: string) => void;
  finishStream: (conversationId: string, messageId: string) => void;
  notify: (text: string, kind?: 'info' | 'error') => void;
  applyEvent: (event: ServerEvent) => void;
}

export const useChat = create<ChatState>((set, get) => ({
  connected: false,
  capabilities: null,
  conversations: [],
  messages: {},
  peers: [],
  activeId: null,
  settingsOpen: false,
  aiMode: false,
  mobileChat: false,
  toast: null,

  setConnected: (connected) => set({ connected }),
  setCapabilities: (capabilities) => set({ capabilities }),
  setConversations: (conversations) => set({ conversations: sortConversations(conversations) }),
  setActive: (activeId) => set({ activeId, mobileChat: Boolean(activeId) }),
  setMessages: (conversationId, messages) =>
    set((state) => ({ messages: { ...state.messages, [conversationId]: [...messages].sort((a, b) => a.ts - b.ts) } })),

  upsertConversation: (conversation) =>
    set((state) => {
      const others = state.conversations.filter((item) => item.id !== conversation.id);
      return { conversations: sortConversations([...others, conversation]) };
    }),

  removeConversation: (id) =>
    set((state) => ({
      conversations: state.conversations.filter((item) => item.id !== id),
      activeId: state.activeId === id ? null : state.activeId,
      messages: Object.fromEntries(Object.entries(state.messages).filter(([key]) => key !== id)),
    })),

  upsertMessage: (message) =>
    set((state) => {
      const list = state.messages[message.conversationId] ?? [];
      return {
        messages: { ...state.messages, [message.conversationId]: mergeMessage(list, message) },
        conversations: sortConversations(
          state.conversations.map((item) =>
            item.id === message.conversationId
              ? {
                  ...item,
                  preview: message.text.slice(0, 120) || item.preview,
                  lastMessageAt: Math.max(item.lastMessageAt, message.ts),
                  unread:
                    message.author.self || state.activeId === message.conversationId
                      ? 0
                      : (item.unread ?? 0) + (state.messages[message.conversationId]?.some((m) => m.id === message.id) ? 0 : 1),
                }
              : item,
          ),
        ),
      };
    }),

  setPeers: (peers) => set({ peers }),
  toggleSettings: (open) => set((state) => ({ settingsOpen: open ?? !state.settingsOpen })),
  toggleAiMode: () => set((state) => ({ aiMode: !state.aiMode })),
  markRead: (conversationId) =>
    set((state) => ({
      conversations: state.conversations.map((item) => (item.id === conversationId ? { ...item, unread: 0 } : item)),
    })),

  addEphemeral: (conversationId, messageId) =>
    set((state) => {
      const message: Message = {
        id: messageId,
        conversationId,
        platform: 'mesh',
        author: { id: 'chatsi-ai', name: 'Chatsi AI', bot: true, self: true },
        text: '…',
        ts: Date.now(),
        streaming: true,
        via: 'ai',
        status: 'sending',
      };
      const list = state.messages[conversationId] ?? [];
      if (list.some((item) => item.id === messageId)) return {};
      return { messages: { ...state.messages, [conversationId]: [...list, message] } };
    }),

  appendStream: (conversationId, messageId, text) =>
    set((state) => {
      const list = state.messages[conversationId] ?? [];
      const exists = list.some((item) => item.id === messageId);
      const base: Message = {
        id: messageId,
        conversationId,
        platform: 'mesh',
        author: { id: 'chatsi-ai', name: 'Chatsi AI', bot: true, self: true },
        text,
        ts: Date.now(),
        streaming: true,
        via: 'ai',
        status: 'sending',
      };
      const next = exists ? list.map((item) => (item.id === messageId ? { ...item, text, streaming: true } : item)) : [...list, base];
      return { messages: { ...state.messages, [conversationId]: next } };
    }),

  finishStream: (conversationId, messageId) =>
    set((state) => ({
      messages: {
        ...state.messages,
        [conversationId]: (state.messages[conversationId] ?? []).map((item) =>
          item.id === messageId ? { ...item, streaming: false, status: 'sent' } : item,
        ),
      },
    })),

  notify: (text, kind = 'info') => {
    set({ toast: { text, kind } });
    setTimeout(() => {
      if (get().toast?.text === text) set({ toast: null });
    }, 4200);
  },

  applyEvent: (event) => {
    const state = get();
    switch (event.type) {
      case 'hello':
        state.setCapabilities(event.capabilities);
        state.setConversations(event.conversations);
        state.setPeers(event.peers);
        break;
      case 'capabilities':
        state.setCapabilities(event.capabilities);
        break;
      case 'conversation':
        state.upsertConversation(event.conversation);
        break;
      case 'message':
        state.upsertMessage(event.message);
        break;
      case 'message.update':
        state.upsertMessage(event.message);
        break;
      case 'presence':
        state.setPeers(event.peers);
        break;
      case 'read':
        state.markRead(event.conversationId);
        break;
      case 'typing':
        break;
      case 'error':
        state.notify(event.message, 'error');
        break;
    }
  },
}));
